import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { ImageFormat } from '@liks/backend-svc-api/esm/models/index';
import type { ImageAspectRatio } from '../../../types/gallery';
import { useLiksChatComposerActions } from './useLiksChatComposerActions';
import { resolveComposerExclusiveUi } from './liksChatComposerExclusiveUi';
import type { LiksChatComposerProps } from './LiksChatComposer.types';
import type { UseLiksChatComposerResult } from './useLiksChatComposer.types';
import { useLiksChatComposerMenuDismiss } from './useLiksChatComposerMenuDismiss';
import { useLiksChatComposerSpeech } from './useLiksChatComposerSpeech';

const MAX_ROWS = 3;

export type { UseLiksChatComposerResult } from './useLiksChatComposer.types';

export function useLiksChatComposer({
  attachLabel,
  addLabel,
  webSearchLabel,
  imageGenerationLabel,
  stagedAttachments = [],
  onAddFiles,
  onRemoveStagedAttachment,
  onSend,
  onSendFailed,
  disabled = false,
  sendDisabled = false,
  knowledgeBaseOptions = [],
  onKnowledgeBaseToggle,
  exclusiveLockLabels,
  hasLinkedKnowledgeBases: hasLinkedKnowledgeBasesProp,
  preferWebSearchDefault = false,
  isPersistedComposerContextReady = true,
  speechInput,
}: LiksChatComposerProps): UseLiksChatComposerResult {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsMenuRef = useRef<HTMLDivElement>(null);
  const knowledgeMenuRef = useRef<HTMLDivElement>(null);
  const imageMenuRef = useRef<HTMLDivElement>(null);
  const [isAttachmentsMenuOpen, setIsAttachmentsMenuOpen] = useState(false);
  const [isKnowledgeMenuOpen, setIsKnowledgeMenuOpen] = useState(false);
  const [isImageMenuOpen, setIsImageMenuOpen] = useState(false);
  const [knowledgeSearchQuery, setKnowledgeSearchQuery] = useState('');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [hasAppliedWebSearchDefault, setHasAppliedWebSearchDefault] =
    useState(false);
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);
  const [imageFormat, setImageFormat] = useState<ImageFormat>('PNG');
  const [imageAspectRatio, setImageAspectRatio] =
    useState<ImageAspectRatio>('1:1');
  const imageFormatGroupName = useId();
  const imageAspectRatioGroupName = useId();
  const [isLocallySendLocked, setIsLocallySendLocked] = useState(false);
  const [prevSendDisabled, setPrevSendDisabled] = useState(sendDisabled);

  const {
    speechElapsedMs,
    isSpeechRecording,
    isSpeechRequesting,
    isSpeechTranscribing,
    isSpeechSessionActive,
    toggleSpeechRecording,
  } = useLiksChatComposerSpeech(speechInput, setMessage);

  const isComposerLockedForSpeech = isSpeechSessionActive;
  const hasUploadingAttachments = stagedAttachments.some(
    (attachment) => attachment.status === 'uploading',
  );
  const hasReadyAttachments = stagedAttachments.some(
    (attachment) => attachment.status === 'ready',
  );
  const isSendBlocked =
    sendDisabled ||
    isLocallySendLocked ||
    isSpeechSessionActive ||
    isSpeechTranscribing;
  const canSend =
    (message.trim().length > 0 || hasReadyAttachments) &&
    !disabled &&
    !isSendBlocked &&
    !hasUploadingAttachments;
  const hasText = message.length > 0;
  const isControlsDisabled = disabled || isComposerLockedForSpeech;

  if (sendDisabled !== prevSendDisabled) {
    setPrevSendDisabled(sendDisabled);
    if (!sendDisabled && isLocallySendLocked) {
      setIsLocallySendLocked(false);
    }
  }

  const filteredKnowledgeBaseOptions = useMemo(() => {
    const normalizedQuery = knowledgeSearchQuery.trim().toLowerCase();
    if (normalizedQuery === '') {
      return knowledgeBaseOptions;
    }
    return knowledgeBaseOptions.filter((option) =>
      option.name.toLowerCase().includes(normalizedQuery),
    );
  }, [knowledgeBaseOptions, knowledgeSearchQuery]);
  const linkedKnowledgeBaseCount = useMemo(
    () => knowledgeBaseOptions.filter((option) => option.isLinked).length,
    [knowledgeBaseOptions],
  );
  const hasLinkedKnowledgeBases =
    hasLinkedKnowledgeBasesProp ?? linkedKnowledgeBaseCount > 0;
  const hasAttachments = stagedAttachments.length > 0;

  if (hasAttachments || hasLinkedKnowledgeBases) {
    if (webSearchEnabled) {
      setWebSearchEnabled(false);
    }
    if (imageGenerationEnabled) {
      setImageGenerationEnabled(false);
    }
  }

  if (
    preferWebSearchDefault &&
    isPersistedComposerContextReady &&
    !hasAppliedWebSearchDefault
  ) {
    setHasAppliedWebSearchDefault(true);
    if (!hasAttachments && !hasLinkedKnowledgeBases) {
      setWebSearchEnabled(true);
    }
  }

  const {
    isAttachmentsLocked,
    isKnowledgeLocked,
    isWebSearchLocked,
    isImageGenerationLocked,
    attachmentsTooltip,
    knowledgeTooltip,
    webSearchTooltip,
    imageGenerationTooltip,
  } = resolveComposerExclusiveUi({
    hasAttachments,
    hasLinkedKnowledgeBases,
    webSearchEnabled,
    imageGenerationEnabled,
    exclusiveLockLabels,
    attachLabel,
    addLabel,
    webSearchLabel,
    imageGenerationLabel,
  });

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const lineHeight = Number.parseFloat(
      window.getComputedStyle(textarea).lineHeight,
    );
    const maxHeight = lineHeight * MAX_ROWS;
    const minHeight = lineHeight;

    textarea.style.maxHeight = `${maxHeight}px`;
    textarea.style.height = 'auto';

    const scrollHeight = textarea.scrollHeight;
    const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [message, resizeTextarea]);

  useLiksChatComposerMenuDismiss({
    isAttachmentsMenuOpen,
    isKnowledgeMenuOpen,
    isImageMenuOpen,
    attachmentsMenuRef,
    knowledgeMenuRef,
    imageMenuRef,
    setIsAttachmentsMenuOpen,
    setIsKnowledgeMenuOpen,
    setIsImageMenuOpen,
  });

  const {
    handleClear,
    handleAddFiles,
    handleRemoveStagedAttachment,
    handleSend,
    handleKnowledgeBaseToggle,
    handleWebSearchToggle,
    handleImageGenerationToggle,
    closeMenusAndToggleSpeech,
  } = useLiksChatComposerActions({
    disabled,
    isSendBlocked,
    hasUploadingAttachments,
    hasReadyAttachments,
    isAttachmentsLocked,
    isKnowledgeLocked,
    isWebSearchLocked,
    isImageGenerationLocked,
    isSpeechTranscribing,
    isSpeechRequesting,
    message,
    setMessage,
    webSearchEnabled,
    imageGenerationEnabled,
    imageFormat,
    imageAspectRatio,
    setIsLocallySendLocked,
    setIsAttachmentsMenuOpen,
    setIsKnowledgeMenuOpen,
    setIsImageMenuOpen,
    setWebSearchEnabled,
    setImageGenerationEnabled,
    textareaRef,
    onAddFiles,
    onRemoveStagedAttachment,
    onSend,
    onSendFailed,
    onKnowledgeBaseToggle,
    toggleSpeechRecording,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isSendBlocked) {
        handleSend();
      }
    }
  };

  return {
    message,
    setMessage,
    textareaRef,
    fileInputRef,
    attachmentsMenuRef,
    knowledgeMenuRef,
    imageMenuRef,
    isAttachmentsMenuOpen,
    setIsAttachmentsMenuOpen,
    isKnowledgeMenuOpen,
    setIsKnowledgeMenuOpen,
    isImageMenuOpen,
    setIsImageMenuOpen,
    knowledgeSearchQuery,
    setKnowledgeSearchQuery,
    webSearchEnabled,
    imageGenerationEnabled,
    imageFormat,
    setImageFormat,
    imageAspectRatio,
    setImageAspectRatio,
    imageFormatGroupName,
    imageAspectRatioGroupName,
    speechElapsedMs,
    isSpeechRecording,
    isSpeechRequesting,
    isSpeechTranscribing,
    canSend,
    hasText,
    isControlsDisabled,
    filteredKnowledgeBaseOptions,
    linkedKnowledgeBaseCount,
    stagedAttachments,
    isAttachmentsLocked,
    isKnowledgeLocked,
    isWebSearchLocked,
    isImageGenerationLocked,
    attachmentsTooltip,
    knowledgeTooltip,
    webSearchTooltip,
    imageGenerationTooltip,
    handleClear,
    handleAddFiles,
    handleRemoveStagedAttachment,
    handleSend,
    handleKnowledgeBaseToggle,
    handleWebSearchToggle,
    handleImageGenerationToggle,
    handleKeyDown,
    closeMenusAndToggleSpeech,
  };
}
